import { Download } from 'lucide-react';
import { formatDuration } from '@/client/lib/date';
import { formatStreamField } from '@/client/lib/format';
import type { EncoderLiveStreamData } from '@shared/types';

import { Button } from '@/client/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  SortableTableHead,
  type SortDirection,
} from '@/client/components/ui/table';

export type LiveStreamSortField = 'simpleName' | 'distinctEventCount' | 'totalDurationMinutes' | 'bitrate';

interface EncoderLiveStreamsTableProps {
  data: EncoderLiveStreamData[];
  totals: {
    distinctEventCount: number;
    totalDurationMinutes: number;
    avgDurationMinutes: number;
  };
  sortField: LiveStreamSortField;
  sortDirection: SortDirection;
  onSort: (field: LiveStreamSortField) => void;
  onExport: () => void;
}

export function EncoderLiveStreamsTable({
  data,
  totals,
  sortField,
  sortDirection,
  onSort,
  onExport,
}: EncoderLiveStreamsTableProps) {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-lg font-semibold">Live Streams by Encoder</h4>
        <Button variant="outline" size="sm" onClick={onExport}>
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableTableHead
                field="simpleName"
                label="Encoder"
                currentSort={sortField}
                currentDirection={sortDirection}
                onSort={onSort}
              />
              <TableHead className="whitespace-nowrap">Resolution</TableHead>
              <SortableTableHead
                field="bitrate"
                label="Bitrate"
                currentSort={sortField}
                currentDirection={sortDirection}
                onSort={onSort}
                className="whitespace-nowrap"
              />
              <TableHead className="whitespace-nowrap">Frame Rate</TableHead>
              <TableHead className="whitespace-nowrap">Aspect</TableHead>
              <TableHead className="whitespace-nowrap">Captions</TableHead>
              <SortableTableHead
                field="distinctEventCount"
                label="Events"
                currentSort={sortField}
                currentDirection={sortDirection}
                onSort={onSort}
                className="text-right whitespace-nowrap"
              />
              <SortableTableHead
                field="totalDurationMinutes"
                label="Total Dur"
                currentSort={sortField}
                currentDirection={sortDirection}
                onSort={onSort}
                className="text-right whitespace-nowrap"
              />
              <TableHead className="text-right whitespace-nowrap">Avg Dur</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-muted-foreground">
                  No live stream data for this period
                </TableCell>
              </TableRow>
            ) : (
              <>
                {data.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{row.simpleName}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatStreamField(row.streamInfo, 'resolution')}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatStreamField(row.streamInfo, 'bitrate')}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatStreamField(row.streamInfo, 'frameRate')}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatStreamField(row.streamInfo, 'aspectRatio')}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatStreamField(row.streamInfo, 'hasCaptions')}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">{row.distinctEventCount}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">{formatDuration(row.totalDurationMinutes)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">{formatDuration(row.avgDurationMinutes)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-muted/50 font-semibold">
                  <TableCell colSpan={6}>Total</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {totals.distinctEventCount}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {formatDuration(totals.totalDurationMinutes)}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {formatDuration(totals.avgDurationMinutes)}
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
